import React from "react";
import { ArrowUpRight, ArrowDownRight, ArrowRight, Plane, Banknote, Ticket, Globe2, Info, Hotel, Clock3, Coins, Users } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const GREEN = "#3E7256";
const DARK = "#0E1712";
const MINT = "#6FC49B";
export const DONUT_COLORS = ["#0E1712", "#0B3B36", "#2F7A5E", "#6FC49B", "#A7E0C2", "#CBD5E1"];

export const DemoBadge = () => (
  <span data-testid="demo-badge" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
    <span className="h-1.5 w-1.5 rounded-full bg-slate-300" /> Démonstration
  </span>
);

export const SourcePill = ({ label }) => (
  <span data-testid="source-pill" className="inline-flex items-center gap-1.5 rounded-full bg-lodge-mint/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-lodge-deep">
    <span className="h-1.5 w-1.5 rounded-full bg-lodge-mint" /> {label}
  </span>
);

export const ScopePill = ({ children, tone = "neutral" }) => {
  const tones = {
    real: "border-emerald-200 bg-emerald-50 text-emerald-700",
    demo: "border-amber-200 bg-amber-50 text-amber-700",
    neutral: "border-slate-200 bg-slate-50 text-slate-500",
  };
  return (
    <span data-testid="scope-pill" className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
};

export const UnavailableBlock = ({ marketLabel, detail, provenance, title, testid = "market-data-unavailable" }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-8 text-center">
    <p className="text-sm font-semibold text-slate-600">{title || "Donnée non disponible pour ce marché"}</p>
    <p className="mx-auto mt-2 max-w-lg text-xs leading-relaxed text-slate-500">
      {detail || `Aucune série spécifique à ${marketLabel || "ce marché"} n’est disponible. Les données d’un autre marché ne sont pas affichées à sa place.`}
    </p>
    {provenance && <p className="mt-3 text-[11px] font-medium text-slate-400">Source attendue : {provenance}</p>}
  </div>
);

export const SectionCard = ({ title, sub, right, demo, source, scope, scopeTone, children, testid, className = "" }) => (
  <section data-testid={testid} className={`rounded-2xl border border-lodge-hairline bg-white p-6 shadow-lodge-sm transition-shadow duration-300 hover:shadow-lodge sm:p-7 ${className}`}>
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-heading text-base font-semibold tracking-tight text-[#1E3D2B]">{title}</h2>
          {source ? <SourcePill label={source} /> : demo ? <DemoBadge /> : null}
          {scope ? <ScopePill tone={scopeTone || (demo ? "demo" : "neutral")}>{scope}</ScopePill> : null}
        </div>
        {sub && <p className="mt-1 text-xs text-lodge-ardoise">{sub}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const ChartTip = ({ active, payload, label, suffix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
      <p className="mb-1 text-[11px] font-medium text-slate-400">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="font-mono text-xs font-semibold" style={{ color: p.color || DARK }}>
          {p.name}: {p.value}{suffix}
        </p>
      ))}
    </div>
  );
};

const KPI_ICONS = { airPassengers: Plane, airRevenue: Banknote, averageFare: Ticket, internationalVisitors: Globe2 };

export const StrategicKpiCards = ({ kpis }) => {
  const list = Object.values(kpis);
  return (
    <div data-testid="strategic-kpis" className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-lodge-hairline bg-lodge-hairline shadow-lodge-sm sm:grid-cols-2 xl:grid-cols-4">
      {list.map((k, i) => {
        const Icon = KPI_ICONS[k.key] || Globe2;
        return (
          <div
            key={k.key}
            data-testid={`mi-kpi-${k.key}`}
            className="animate-fade-up bg-white p-7"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  {k.label}
                  {k.tooltip && (
                    <span data-testid={`mi-kpi-${k.key}-tooltip`} title={k.tooltip} className="inline-flex cursor-help text-slate-300 hover:text-slate-400">
                      <Info size={12} />
                    </span>
                  )}
                </p>
                {k.scope && <span data-testid={`mi-kpi-${k.key}-scope`} className="mt-1 block text-[10px] font-semibold uppercase tracking-wide text-[#3E7256]">{k.scope}</span>}
              </div>
              <Icon size={16} className="text-[#3E7256]" />
            </div>
            <div className="mt-5 flex min-h-[36px] items-baseline gap-1">
              {k.loading ? (
                <span
                  data-testid={`mi-kpi-${k.key}-loading`}
                  className="h-8 w-28 animate-pulse rounded-lg bg-slate-100"
                  aria-label="Chargement"
                />
              ) : k.pending ? (
                <span data-testid={`mi-kpi-${k.key}-unavailable`} className="font-heading text-lg font-semibold tracking-tight text-slate-400">Donnée non disponible</span>
              ) : (
                <>
                  <span className="font-heading text-3xl font-bold tracking-tight text-[#1E3D2B]">{k.value}</span>
                  <span className="text-base font-semibold text-slate-300">{k.unit}</span>
                </>
              )}
            </div>
            {!k.loading && <p className="mt-1.5 text-xs text-slate-400">{k.sub}</p>}
            <div className="mt-4">
              {k.loading ? (
                <span className="block h-5 w-24 animate-pulse rounded-full bg-slate-100" aria-hidden="true" />
              ) : k.pending ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{k.actionLabel || "Bientôt connecté"}</span>
              ) : k.source ? (
                <SourcePill label={k.source} />
              ) : (
                <p className={`inline-flex items-center gap-1 text-xs font-semibold ${k.positive ? "text-emerald-600" : "text-rose-500"}`}>
                  {k.positive ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                  {k.delta}<span className="ml-1 font-normal text-slate-400">{k.deltaLabel || "vs période préc."}</span>
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AirlineRanking = ({ items, unit, valueFormat, formatItem, footerLabel, testid }) => {
  const max = Math.max(...items.map((a) => a.share), 1);
  return (
    <div data-testid={testid} className="space-y-5">
      {items.map((a) => (
        <div key={a.rank} data-testid={`${testid}-row-${a.rank}`}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="flex min-w-0 flex-1 items-center gap-3 text-sm font-medium text-slate-700">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lodge-mint/20 text-[11px] font-bold text-lodge-deep">{a.rank}</span>
              <span className="truncate" title={a.name}>{a.name}</span>
            </span>
            <span className="flex shrink-0 flex-col items-end leading-tight">
              <span className="font-mono text-xs font-semibold text-[#1E3D2B]">
                {formatItem ? formatItem(a) : valueFormat ? valueFormat(a.value) : `${a.value} ${unit}`}
              </span>
              <span
                data-testid={`${testid}-share-${a.rank}`}
                className="mt-0.5 whitespace-nowrap text-[11px] font-medium text-lodge-ardoise"
              >
                <span className="hidden sm:inline">Part de marché : </span>{a.share} %
              </span>
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-lodge-mint" style={{ width: `${(a.share / max) * 100}%` }} />
          </div>
        </div>
      ))}
      <button className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
        {footerLabel} <ArrowRight size={13} />
      </button>
    </div>
  );
};

export const MonthlyEvolutionChart = ({ data, realTourism = false }) => (
  <ResponsiveContainer width="100%" height={300} data-testid="monthly-evolution-chart">
    <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#F3F0E7" vertical={false} />
      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
      <YAxis tick={{ fontSize: 11, fill: "#CBD5E1" }} axisLine={false} tickLine={false} />
      <Tooltip content={<ChartTip suffix={realTourism ? "" : " M"} />} />
      <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
      {!realTourism && <Line type="monotone" name="Passagers" dataKey="passengers" stroke={DARK} strokeWidth={2.5} dot={false} isAnimationActive={false} activeDot={{ r: 4 }} />}
      <Line type="monotone" name={realTourism ? "Arrivées visiteurs" : "Visiteurs"} dataKey="visitors" stroke={realTourism ? DARK : MINT} strokeWidth={2.5} strokeDasharray={realTourism ? undefined : "5 4"} dot={false} isAnimationActive={false} activeDot={{ r: 4 }} />
    </LineChart>
  </ResponsiveContainer>
);

export const SeasonalityChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={200} data-testid="seasonality-chart">
    <BarChart data={data} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
      <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#CBD5E1" }} axisLine={false} tickLine={false} interval={0} />
      <YAxis hide />
      <Tooltip content={<ChartTip />} cursor={{ fill: "#FAFAF6" }} />
      <Bar dataKey="value" fill="#0B3B36" radius={[3, 3, 0, 0]} barSize={12} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);

const TOURISM_KPI_ICONS = { arrivals: Users, averageStay: Clock3, receipts: Coins, roomOccupancy: Hotel };

export const TourismKpiGrid = ({ items }) => (
  <div data-testid="tourism-kpis" className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-lodge-hairline bg-lodge-hairline shadow-lodge-sm sm:grid-cols-2 xl:grid-cols-4">
    {items.map((item) => {
      const Icon = TOURISM_KPI_ICONS[item.key] || Globe2;
      return (
        <div key={item.key} data-testid={`tourism-kpi-${item.key}`} className="bg-white p-6 sm:p-7">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
              {item.scope && <span data-testid={`tourism-kpi-${item.key}-scope`} className="mt-1 block text-[10px] font-semibold uppercase tracking-wide text-amber-700">{item.scope}</span>}
            </div>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-lodge-mint/20 text-lodge-deep"><Icon size={16} /></span>
          </div>
          <p className="mt-5 font-heading text-3xl font-bold tracking-tight text-[#1E3D2B]">
            {item.value}<span className="ml-1 text-base font-semibold text-slate-300">{item.unit}</span>
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{item.sub}</p>
          <div className="mt-4"><SourcePill label="Lodge Data" /></div>
        </div>
      );
    })}
  </div>
);

export const OccupancyChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={260} data-testid="occupancy-chart">
    <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#F3F0E7" vertical={false} />
      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} dy={8} />
      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#CBD5E1" }} axisLine={false} tickLine={false} unit="%" />
      <Tooltip content={<ChartTip suffix=" %" />} />
      <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
      <Line type="monotone" name="Chambres" dataKey="rooms" connectNulls stroke={DARK} strokeWidth={2.5} dot={false} isAnimationActive={false} activeDot={{ r: 4 }} />
      <Line type="monotone" name="Lits" dataKey="beds" connectNulls stroke={MINT} strokeWidth={2.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} activeDot={{ r: 4 }} />
    </LineChart>
  </ResponsiveContainer>
);

export const CrossDataInsights = ({ items }) => (
  <div data-testid="cross-data-insights" className="grid grid-cols-1 gap-4 md:grid-cols-2">
    {items.map((item) => (
      <article key={item.id} data-testid={`cross-insight-${item.id}`} className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm sm:p-6">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-lodge-mint" />
          <h3 className="font-heading text-sm font-semibold text-[#1E3D2B]">{item.title}</h3>
        </div>
        {item.scope && <span data-testid={`cross-insight-${item.id}-scope`} className="mt-2 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{item.scope}</span>}
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.text}</p>
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500"><strong className="text-[#3E7256]">Implication — </strong>{item.implication}</p>
      </article>
    ))}
  </div>
);

export const VisitorOriginsDonut = ({ data }) => (
  <div className="flex min-w-0 flex-col gap-5" data-testid="visitor-origins">
    <div className="mx-auto h-44 w-full min-w-0 max-w-[15rem] overflow-hidden sm:h-48">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius="57%" outerRadius="88%" paddingAngle={2} stroke="none" isAnimationActive={false}>
            {data.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
          </Pie>
          <Tooltip content={<ChartTip suffix=" %" />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <div className="w-full min-w-0 space-y-1.5" data-testid="visitor-origins-legend">
      {data.map((o, i) => (
        <div key={o.name} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-1 py-1.5 text-sm">
          <span className="flex min-w-0 items-center gap-2 text-slate-600">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="min-w-0 break-words leading-snug">{o.name}</span>
          </span>
          <span
            data-testid={`visitor-origin-value-${i}`}
            className="inline-flex min-w-[4.75rem] shrink-0 justify-end whitespace-nowrap rounded-full bg-lodge-mint/15 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-lodge-deep"
          >
            {o.value}{"\u00A0"}%
          </span>
        </div>
      ))}
    </div>
  </div>
);

const Spark = ({ points }) => {
  const w = 60, h = 20;
  const min = Math.min(...points), max = Math.max(...points), span = max - min || 1;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p - min) / span) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={d} fill="none" stroke="#6FC49B" strokeWidth="1.5" />
    </svg>
  );
};

export const EconomicContextCard = ({ items }) => (
  <div className="grid grid-cols-2 gap-6" data-testid="economic-context">
    {items.map((e) => (
      <div key={e.key || e.label} data-testid={`economic-indicator-${e.key || "item"}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{e.label}</p>
        <p className="mt-1 font-heading text-xl font-bold text-[#1E3D2B]">{e.value}</p>
        <div className="mt-2 flex items-center justify-between">
          <span className={`text-[11px] font-semibold ${e.positive ? "text-emerald-600" : "text-rose-500"}`}>{e.delta}</span>
          {e.trend?.length > 1 && <Spark points={e.trend} />}
        </div>
        {e.source?.provider && (
          <p className="mt-1 text-[10px] text-slate-400" data-testid={`economic-source-${e.key || "item"}`}>
            {e.source.provider}{e.period ? ` · ${e.period}` : ""}
          </p>
        )}
      </div>
    ))}
  </div>
);

export const DataSourcesCard = ({ items }) => (
  <ul className="space-y-3" data-testid="data-sources">
    {items.map((s) => (
      <li key={s.name} className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">{s.name}</p>
          <p className="text-xs text-slate-400">{s.desc}</p>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${s.status === "Connecté" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.status}</span>
      </li>
    ))}
  </ul>
);

const PRIORITY_STYLE = {
  "Élevé": "bg-[#1E3D2B] text-white",
  "Moyen": "bg-lodge-mint/20 text-lodge-deep",
  "Faible": "bg-slate-100 text-slate-500",
};

export const LodgeIntelligenceSignals = ({ signals = [] }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="intelligence-signals">
    {signals.map((s, index) => {
      const id = s.id || `${s.type || "market-signal"}-${index}`;
      const category = s.category || "Signal marché";
      const title = s.title || s.type;
      const insight = s.insight || s.detail;
      const metrics = Array.isArray(s.metrics) ? s.metrics : [];
      return (
        <div key={id} data-testid={`signal-${id}`} className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#3E7256]/40">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#3E7256]">{category}</span>
            {s.priority && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[s.priority] || "bg-slate-100 text-slate-500"}`}>{s.priority}</span>}
          </div>
          <h3 className="font-heading text-sm font-bold text-[#1E3D2B]">{title}</h3>
          <p className="mt-2 flex-1 text-xs leading-relaxed text-slate-500">{insight}</p>
          {metrics.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5">
            {metrics.map((m) => (
              <span key={m.label} className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                {m.label}: <span className="font-semibold text-[#1E3D2B]">{m.value}</span>
              </span>
            ))}
          </div>}
          {s.cta && <button className="mt-4 inline-flex items-center gap-1 self-start text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">
            {s.cta} <ArrowRight size={13} />
          </button>}
          {Array.isArray(s.sources) && s.sources.length > 0 && (
            <p className="mt-3 text-[10px] text-slate-400" data-testid={`signal-sources-${id}`}>
              Sources : {s.sources.join(" · ")}
            </p>
          )}
        </div>
      );
    })}
  </div>
);

const NEWS_CAT_COLOR = {
  Aviation: "text-sky-600 bg-sky-50",
  Tourism: "text-emerald-600 bg-emerald-50",
  Hospitality: "text-amber-600 bg-amber-50",
  Economy: "text-violet-600 bg-violet-50",
};

export const LiveMarketSignals = ({ items }) => (
  <ul className="divide-y divide-slate-100" data-testid="live-signals">
    {items.map((n, i) => (
      <li key={n.article_id || n.url || i} className="flex items-start gap-3 py-3.5 first:pt-0 last:pb-0">
        <span className={`mt-0.5 shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold ${NEWS_CAT_COLOR[n.category] || "bg-slate-100 text-slate-500"}`}>{n.category}</span>
        <div className="min-w-0 flex-1">
          {n.url ? (
            <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-[#1E3D2B] hover:underline">
              {n.title || n.text}
            </a>
          ) : <p className="text-sm text-slate-600">{n.title || n.text}</p>}
          {(n.publisher || n.source?.publisher) && (
            <p className="mt-1 truncate text-[11px] text-slate-400">
              Source : {n.publisher || n.source.publisher}
              {n.source_country ? ` · Pays source : ${n.source_country}` : ""}
              {n.scope_label ? ` · Marché : ${n.scope_label}` : ""}
            </p>
          )}
        </div>
        <time dateTime={n.published_at || undefined} className="shrink-0 text-[11px] text-slate-400">
          {n.published_at
            ? new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(n.published_at))
            : n.time}
        </time>
      </li>
    ))}
  </ul>
);
